import { Component, Inject, NgZone, OnDestroy, OnInit, Optional } from '@angular/core';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { Item } from '@app/core/components/listbox/listbox.component';
import { ProgressModalComponent } from '@app/core/components/grid/modals/progress-modal/progress-modal.component';
import { TtScreenBlockService } from '@app/core/components/tt-screen-block/tt-screen-block.service';
import { LayoutService } from '@app/core/services/layout.service';
import { ModalService } from '@app/core/services/modal.service';
import { RememberService } from '@app/core/services/remember.service';
import { parseNumber } from '@app/core/services/utility.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { ConsignmentColliItem, ConsignmentDeliveryMethod, ConsignmentGet, ConsignmentPrePickItem, ConsignmentService } from '@app/services/consignment.service';
import { CargonizerPrinter, CargonizerService } from '@app/services/cargonizer.service';
import { LogisticService, StockLocation } from '@app/services/logistic.service';
import { ProductKeynameItem, TypeaheadService } from '@app/services/typeahead.service';

@Component({
  selector: 'tt-consignment',
  templateUrl: './consignment.component.html',
  styleUrls: ['./consignment.component.css'],
  standalone: false,
})
export class ConsignmentComponent implements OnInit, OnDestroy {
    public page?: ConsignmentGet;
    public gridLines?: GridOptions<ConsignmentGridLine>;
    public gridColli?: GridOptions;
    public orderLines: Item[] = [];
    public pickLines: Item[] = [];
    public manualPickLines: Item[] = [];
    public prePickLines: ConsignmentPrePickItem[] = [];
    public collis: ConsignmentColliItem[] = [];
    public deliveryMethods: ConsignmentDeliveryMethod[] = [];
    public stockLocations: StockLocation[] = [];
    public shipments: Item[] = [];
    public printers: CargonizerPrinter[] = [];
    public addressTab = 'a';
    public addressInfoTab = 'i';
    public otherTab = 'OTHER';
    public autoTab = 'SELECT';
    public isDesktop = false;
    public isCargonizer = false;
    public isDirty = false;
    public loading = false;
    public busy = '';
    public freightSearch = '';
    private keyno?: string | number;
    private readonly tabletQuery: MediaQueryList;
    private destroyed = false;
    private pageVersion = 0;
    private colliVersion = 0;
    private freightVersion = 0;
    private editVersion = 0;
    private stockTimer?: ReturnType<typeof setTimeout>;
    private progress?: ProgressModalComponent;
    private previousDeliveryMethod: string | number | null = null;
    private readonly onBreakpointChanged = (event: MediaQueryListEvent) => this.zone.run(() => {
        this.isDesktop = event.matches;
        if (this.page) void this.refreshPicking();
    });

    constructor(
        private consignment: ConsignmentService, private cargonizer: CargonizerService,
        private logistic: LogisticService, private typeahead: TypeaheadService,
        private modal: ModalService, private remember: RememberService,
        private screenBlock: TtScreenBlockService, layout: LayoutService, private zone: NgZone,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
        this.tabletQuery = layout.getMediaQueries().tablet;
        this.isDesktop = this.tabletQuery.matches;
    }

    public async ngOnInit(): Promise<void> {
        const keyno = this.navigator?.params['consignment_keyno'];
        if (typeof keyno !== 'string' && typeof keyno !== 'number') return;
        this.keyno = keyno;
        this.tabletQuery.addEventListener('change', this.onBreakpointChanged);
        this.gridLines = new Grid<ConsignmentGridLine>()
            .setRememberId('w_consignment_consignmentline_grid')
            .setLoadDataFunction({ method: 3556, parameters: { consignment_keyno: keyno } })
            .setKendoConfig({ height: null, sortable: true, aggregate: true, pager: false, selectable: false, filterable: false })
            .setCustomToolbarButtons([
                { name: 'consigment_consignmentline_delete_assignments', text: 'consigment_consignmentline_delete_assignments', icon: 'fas fa-trash-alt', translate: true, disable_if_no_selected: true, func: () => this.deleteAssignments() },
                { name: 'consignment_assign', text: 'consignment_assign', icon: 'fas fa-sync-alt', translate: true, func: () => this.assign() },
            ]);
        this.gridColli = new Grid().setRememberId('w_consignment.colli.grid')
            .setLoadDataFunction({ method: 3810, parameters: () => this.page });
        await this.loadPage();
    }

    public async loadPage(preserveTabs = false): Promise<void> {
        if (this.keyno === undefined || this.destroyed) return;
        const version = ++this.pageVersion;
        this.loading = true;
        const tabs = this.page && {
            bg_consignmentselection: this.page.bg_consignmentselection,
            bg_consignment_packing_selection: this.page.bg_consignment_packing_selection,
            bg_ConsignmentPickSelection: this.page.bg_ConsignmentPickSelection,
        };
        try {
            const params = { consignment_keyno: this.keyno };
            const page = await this.consignment.getConsignment(params);
            if (!page || this.destroyed || version !== this.pageVersion) return;
            if (preserveTabs && tabs) Object.assign(page, tabs);
            this.page = page;
            this.isDirty = false;
            this.freightSearch = page.prod_id_freight;
            this.previousDeliveryMethod = page.deliverymethod_no;
            const [orderLines, pickLines, manualPickLines, deliveryMethods, stockLocations, shipments, prePickLines, printers] = await Promise.all([
                this.consignment.getOrderLines(params), this.consignment.getPickLines(params), this.consignment.getManualPickLines(params),
                this.consignment.getDeliveryMethods(params),
                page.stockplace_id_from ? this.logistic.getStockLocations(page.stockplace_id_from) : Promise.resolve([]),
                page.stockplace_id_from ? this.consignment.getConsignmentShipments({ stockplace_id: page.stockplace_id_from }) : Promise.resolve([]),
                this.number(page.stocktransfer_keyno) > 0 ? this.consignment.getPrePickLines({ stocktransfer_keyno: page.stocktransfer_keyno }) : Promise.resolve([]),
                this.cargonizer.getPrinters(params),
            ]);
            if (this.destroyed || version !== this.pageVersion) return;
            Object.assign(this, { orderLines, pickLines, manualPickLines, deliveryMethods, stockLocations, shipments, prePickLines, printers });
            this.updateDeliveryMethod();
            if (page.bg_consignmentselection === 'packing') await this.loadCollis();
        } finally { if (version === this.pageVersion) this.loading = false; }
    }

    public number(value: unknown): number {
        return typeof value === 'string' || typeof value === 'number' ? parseNumber(value) || 0 : 0;
    }

    public async onMainTabChanged(value: string): Promise<void> {
        if (!this.page) return;
        this.page.bg_consignmentselection = value;
        if (value === 'packing') await this.loadCollis();
    }

    public async onPackingTabChanged(value: string): Promise<void> {
        if (!this.page) return;
        this.page.bg_consignment_packing_selection = value;
        await this.loadCollis();
    }

    public onFieldChanged(field: string, value: unknown): void {
        if (!this.page || this.page[field] === value || value === undefined) return;
        this.page[field] = value;
        this.isDirty = true;
        ++this.editVersion;
        if (['length_cm', 'width_cm', 'height_cm'].includes(field) && this.page.setting_use_colli_dimensions !== '1') {
            this.page.volum = this.number(this.page.length_cm) * this.number(this.page.width_cm) * this.number(this.page.height_cm) / 1000000;
        }
        if (field === 'deliverymethod_no') this.updateDeliveryMethod();
        if (field === 'stocklocation_keyno') {
            clearTimeout(this.stockTimer);
            this.stockTimer = setTimeout(() => { void this.save(); }, 50);
        }
    }

    private updateDeliveryMethod(): void {
        if (!this.page) return;
        if (this.page.deliverymethod_no === null) this.page.deliverymethod_no = this.previousDeliveryMethod;
        const method = this.deliveryMethods.find(item => String(item.deliverymethod_no) === String(this.page?.deliverymethod_no));
        this.isCargonizer = !!method && method.deliverymethod_cargonizer !== '0' && method.deliverymethod_cargonizer !== '';
        if (method) this.page.send_delivery_confirmation = method.send_delivery_confirmation;
    }

    public setSelection(kind: 'pick' | 'colli', id: string | number, selected: unknown): void {
        const value = selected === true || selected === '1' ? '1' : '0';
        const updated = (kind === 'pick' ? this.pickLines : this.collis).map(row => row.item_id === id ? { ...row, item_is_selected: value as '1' | '0' } : row);
        if (kind === 'pick') this.pickLines = updated;
        else this.collis = updated;
    }

    public selectAll(kind: 'pick' | 'colli', selected: boolean): void {
        const updated = (kind === 'pick' ? this.pickLines : this.collis).map(row => ({ ...row, item_is_selected: selected ? '1' as const : '0' as const }));
        if (kind === 'pick') this.pickLines = updated;
        else this.collis = updated;
    }

    public get allPickedSelected(): boolean { return this.pickLines.length > 0 && this.pickLines.every(row => row.item_is_selected === '1'); }

    public async loadCollis(): Promise<void> {
        const page = this.page;
        if (!page || this.number(page.consignment_keyno) <= 0) return;
        const version = ++this.colliVersion;
        const collis = await this.consignment.getCollis({ consignment_keyno: page.consignment_keyno, retreive_type: page.bg_consignment_packing_selection });
        if (this.destroyed || version !== this.colliVersion || page !== this.page) return;
        this.collis = collis;
        page.weight = collis.reduce((sum, item) => sum + this.number(item.item_weight), 0);
        if (page.show_xyz === '0') page.volum = collis.reduce((sum, item) => sum + this.number(item.item_volume), 0);
        page.colli = collis.length;
        this.gridColli?.gridfunc?.read();
    }

    private async warning(message: string, type: 'warning' | 'danger' = 'warning'): Promise<void> {
        await this.modal.show({ type, title: 'error', translateTitle: true, message });
    }

    private async action(name: string, work: () => Promise<void>, block = false): Promise<void> {
        if (this.destroyed || !this.page || this.loading || this.busy) return;
        this.busy = name;
        if (block) this.screenBlock.start('message');
        try { await work(); }
        finally { if (block) this.screenBlock.stop(); this.busy = ''; }
    }

    private async saveHeader(): Promise<boolean> {
        if (!this.page) return false;
        const version = this.editVersion;
        const response = await this.consignment.saveConsignment({ ...this.page });
        const saved = response?.saveok === '1';
        if (saved && version === this.editVersion) this.isDirty = false;
        return saved;
    }

    public save(): Promise<void> { return this.action('save', async () => { await this.saveHeader(); }); }

    private async refreshPicking(): Promise<void> {
        if (this.keyno === undefined) return;
        const version = this.pageVersion;
        const rows = await this.consignment.getPickLines({ consignment_keyno: this.keyno });
        if (!this.destroyed && version === this.pageVersion) { this.pickLines = rows; this.gridLines?.gridfunc?.read(); }
    }

    public assign(): Promise<void> {
        return this.action('assign', async () => {
            const response = await this.consignment.assign({ consignment_keyno: this.page!.consignment_keyno });
            if (response.errorcode !== '0') await this.warning(response.errormessage);
            await this.refreshPicking();
        });
    }

    public pickSelected(): Promise<void> {
        return this.action('pick', async () => {
            await this.consignment.selectPickLines(this.pickLines);
            const response = await this.consignment.assign({ consignment_keyno: this.page!.consignment_keyno });
            if (response.errorcode !== '0') await this.warning(response.errormessage);
            await this.refreshPicking();
        });
    }

    public finishPrePick(): Promise<void> {
        return this.action('prepick', async () => {
            const response = await this.consignment.finishPrePick({ stocktransfer_keyno: this.page!.stocktransfer_keyno });
            if (response.errorcode !== '0') { await this.warning(response.errormessage); return; }
            const assigned = await this.consignment.assign({ consignment_keyno: this.page!.consignment_keyno });
            if (assigned.errorcode !== '0') await this.warning(assigned.errormessage);
            await this.loadPage();
        });
    }

    public changeCollis(split: boolean): Promise<void> {
        return this.action(split ? 'split' : 'merge', async () => {
            const params = { consignment_keyno: this.page!.consignment_keyno, data: this.collis };
            if (split) await this.consignment.splitCollis(params); else await this.consignment.mergeCollis(params);
            await this.loadCollis();
        });
    }

    public deleteAssignments(): Promise<void> {
        return this.action('deleteAssignments', async () => {
            const rows = this.gridLines?.gridfunc?.getIsSelectedRows() ?? [];
            if (!rows.length) return;
            const progress = this.progress = this.modal.openProgressDialog({ value: 0, max: rows.length, label: 'progressbar', showCancel: true, rememberId: 'w_consignment_consignmentline_grid' });
            const cancelled = () => this.destroyed || progress.data.status === 'cancelled';
            try {
                for (const row of rows) {
                    if (cancelled()) return;
                    const response = await this.consignment.deleteLineAssignment({ consignmentline_keyno: row.consignmentline_keyno });
                    if (cancelled()) return;
                    if (response.errorcode !== '0') {
                        progress.updateInvalidText(response.errormessage); progress.updateStatus('invalid');
                        await this.warning(response.errormessage); return;
                    }
                    progress.updateValue(progress.data.value + 1);
                }
                progress.updateStatus('finish'); this.gridLines?.gridfunc?.read();
            } finally { this.progress = undefined; }
        });
    }

    public async onManualPick(event: { item: Item; event: MouseEvent }): Promise<void> {
        await this.remember.remember('active_consignmentline_keyno', event.item.item_id);
        if (!this.destroyed) this.navigateItem(event);
    }

    public async onPrePick(event: { item: ConsignmentPrePickItem; event: MouseEvent }): Promise<void> {
        await this.remember.remember('consignment_prepick_special_product', event.item.special_product ?? '');
        if (!this.destroyed) this.navigateItem(event, 'stocktransferloaditem');
    }

    public navigateItem(event: { item: Item; event: MouseEvent }, state = event.item.item_state): void {
        if (!state) return;
        const target = { to: state, parms: event.item.item_parms };
        if (event.event.ctrlKey || event.event.metaKey) void this.navigator?.newTab(target); else void this.navigator?.go(target);
    }

    public openUrl(url: string): void { if (url) window.open(url, '_blank', 'noopener'); }

    public openContact(protocol: 'mailto' | 'tel', value: string): void {
        if (value) window.location.assign(`${protocol}:${value}`);
    }

    public confirmDelivery(): Promise<void> {
        return this.action('confirm', async () => {
            const params = { consignment_keyno: this.page!.consignment_keyno };
            this.screenBlock.setMessage('Prevalidering..');
            const validation = await this.consignment.prevalidate(params);
            if (validation.status_no !== '0') { await this.warning(validation.status_message); return; }
            const delivered = await this.consignment.confirmDelivery({ ...this.page! });
            if (delivered.validationcode !== '0') { await this.warning(delivered.validationmessage); return; }
            this.screenBlock.setMessage('Updating external party');
            await this.consignment.completeWebshop(params);
            if (!this.destroyed) await this.navigator?.back();
        }, true);
    }

    public createCargonizer(): Promise<void> {
        return this.action('cargonizer', async () => {
            if (!await this.saveHeader()) return;
            const page = this.page!;
            const params = { consignment_keyno: page.consignment_keyno, deliverymethod_no: page.deliverymethod_no, colli: page.colli, weight: page.weight, volum: page.volum };
            this.screenBlock.setMessage('Validating consignment');
            const validation = await this.cargonizer.validate(params);
            if (validation.status !== '0') { await this.warning(validation.status_message, 'danger'); return; }
            if (!page.tracking_url) {
                const prevalidation = await this.consignment.prevalidate({ consignment_keyno: page.consignment_keyno });
                if (prevalidation.status_no !== '0') { await this.warning(prevalidation.status_message); return; }
                const created = await this.cargonizer.create(params);
                if (created.errorcode !== '0') { await this.warning(created.errormessage); return; }
                if (this.number(created.consignment_keyno) > 0) await this.consignment.confirmDelivery({ ...page });
                await this.loadPage(true);
                this.screenBlock.setMessage('Updating external party');
                await this.consignment.completeWebshop({ consignment_keyno: page.consignment_keyno });
            }
            this.screenBlock.setMessage('Printing labels');
            const printed = await this.cargonizer.printLabels({ consignment_keyno: page.consignment_keyno, cargonizer_printer_id: page.cargonizer_printer_id });
            if (printed.errorcode !== '0') await this.warning(printed.errormessage);
        }, true);
    }

    public deleteConsignment(): Promise<void> {
        return this.action('delete', async () => {
            const params = { consignment_keyno: this.page!.consignment_keyno };
            const response = await this.consignment.checkDelete(params);
            if (response.errorcode !== '0' && !await this.modal.openConfirmDialog({ title: 'confirm_title', translateTitle: true, message: response.errormessage })) return;
            await this.consignment.deleteConsignment(params);
            if (!this.destroyed) await this.navigator?.back();
        });
    }

    public rollback(): Promise<void> {
        return this.action('rollback', async () => {
            const response = await this.consignment.rollback({ consignment_keyno: this.page!.consignment_keyno });
            if (response.errorcode !== '0') await this.warning(response.errormessage); else await this.loadPage();
        });
    }

    public searchFreightProducts = (params?: { value?: string }): Promise<ProductKeynameItem[]> => this.typeahead.searchProductsKeyname({ searchtext: params?.value ?? '' });

    public async onFreightSelected(item?: ProductKeynameItem): Promise<void> {
        if (!this.page) return;
        const page = this.page;
        const version = ++this.freightVersion;
        page.prod_id_freight = item?.prod_id ?? '';
        this.freightSearch = item?.prod_keyname ?? '';
        if (!item?.prod_id) return;
        const response = await this.consignment.getOrderFreight({ consignment_keyno: page.consignment_keyno, prod_id: item.prod_id });
        if (!this.destroyed && page === this.page && version === this.freightVersion && response?.sum_amount_net_locval) page.order_freight = response.sum_amount_net_locval;
    }

    public estimateFreight(): Promise<void> {
        return this.action('estimate', async () => {
            const page = this.page!;
            const response = await this.consignment.estimateFreight({ consignment_keyno: page.consignment_keyno, deliverymethod_no: page.deliverymethod_no, colli: page.colli, weight: page.weight, volum: page.volum });
            if (response.errorcode === '0') page.est_freight = response.estimated_cost; else await this.warning(response.errormessage, 'danger');
        });
    }

    public applyFreight(): Promise<void> {
        return this.action('freight', async () => {
            const page = this.page!;
            const response = await this.consignment.applyFreight({ consignment_keyno: page.consignment_keyno, prod_id_freight: page.prod_id_freight, est_freight: page.est_freight });
            if (!response.iserror) await this.loadPage(true);
        });
    }

    public ngOnDestroy(): void {
        this.destroyed = true; ++this.pageVersion; ++this.colliVersion; ++this.freightVersion;
        clearTimeout(this.stockTimer);
        this.tabletQuery.removeEventListener('change', this.onBreakpointChanged);
        this.progress?.dialogRef.close();
        if (this.busy === 'confirm' || this.busy === 'cargonizer') this.screenBlock.stop();
    }
}

interface ConsignmentGridLine extends Record<string, unknown> { consignmentline_keyno: string | number; }
